import type { CartPeoplePayload } from './interfaces';
import type { PaymentType } from './types';
import { getPayloadDateString } from '@Helpers';
import { CHECK_IN_INTAKE_TYPES } from './constants';

function getPaymentMethodTranslation(title: PaymentType) {
  // prettier-ignore
  return title === 'card_reader' ? 'cart.paymentOptions.cardReader' :
         title === 'cash' ? 'cart.paymentOptions.cash' :
         title === 'check' ? 'cart.paymentOptions.check' :
         title === 'credit_card' ? 'cart.paymentOptions.creditCard' :
         title === 'split_payment' ? 'cart.paymentOptions.splitPayment' :
        'cart.paymentOptions.other';
}

function getSourceTranslation(source: string) {
  // prettier-ignore
  return source === 'mobile_checkout' ? 'report.endOfDay.mobile' :
    source === 'manual' ? 'report.endOfDay.manual' :
    '-';
}

/**
 * Determines if an intake type should use "Check In" wording
 * @param intakeType - The intake type form_name (e.g., 'clinic_in', 'stray')
 * @returns true if the intake type should use "Check In" wording, false otherwise
 */
function isCheckInIntakeType(intakeType: string | null | undefined): boolean {
  if (!intakeType) return false;

  return (CHECK_IN_INTAKE_TYPES as readonly string[]).includes(intakeType);
}

/**
 * Determines if an intake type should use "Intake" wording
 * @param intakeType - The intake type form_name (e.g., 'stray', 'clinic_in')
 * @returns true if the intake type should use "Intake" wording, false otherwise
 * Note: This is the inverse of isCheckInIntakeType - if it's not a "Check In" type, it uses "Intake" wording
 */
function isIntakeWordingType(intakeType: string | null | undefined): boolean {
  if (!intakeType) return false;

  return !isCheckInIntakeType(intakeType);
}

/**
 * Maps a person document (people store / get-people-profile shape) to the payload
 * shape expected by the intake cart (add-person-to-cart). Use when starting
 * intake from a People profile so the cart has full contact info.
 * @param profile - Person document (may include custom fields at top level)
 * @param formFields - Optional array of form field definitions with .name; when provided, adds every form field value present on the profile (including custom fields)
 */
function mapPersonProfileToIntakeCartPayload(
  profile: any,
  formFields?: { name: string; type: string }[]
): CartPeoplePayload | null {
  if (!profile) return null;

  const addr = profile?.addresses?.[0] ?? {};
  const phoneObj = profile?.phones?.[0];
  const emailObj = profile?.emails?.[0];
  const phone = phoneObj?.phone ?? phoneObj ?? '';
  const email = emailObj?.email ?? emailObj ?? '';
  const personId = profile?._id?.$oid ?? profile?._id ?? '';
  const formId = profile?.form_id?.$oid ?? profile?.form_id ?? null;

  const address = {
    address1: addr.address1 ?? null,
    address2: addr.address2 ?? null,
    city: addr.city ?? null,
    country: addr.country ?? null,
    state: addr.state ?? null,
    zip: addr.zip ?? null,
  };

  const payload: CartPeoplePayload = {
    address1: addr.address1 ?? null,
    address2: addr.address2 ?? null,
    city: addr.city ?? null,
    country: addr.country ?? null,
    email: email || null,
    first_name: profile?.first_name ?? null,
    last_name: profile?.last_name ?? null,
    phone: phone || null,
    state: addr.state ?? null,
    zip: addr.zip ?? null,
    formId,
    outcome_type: 'intake',
    person_id: personId,
    already_registered: true,
    already_adopted: !!profile?.already_adopted,
    email_index: profile?.matching_email_index ?? 0,
    phone_index: profile?.matching_phone_index ?? 0,
    phone_type: (phoneObj as any)?.type ?? 'mobile',
    address,
  };

  const validFormFields = formFields && Array.isArray(formFields) && formFields?.length > 0;
  if (validFormFields) {
    formFields.forEach(({ name, type }) => {
      if (!name || profile[name] === undefined) return;

      let match = profile[name];

      if (type === 'date') {
        match = getPayloadDateString(match);
      }

      payload[name] = match;
    });
  }

  return payload;
}

export {
  getPaymentMethodTranslation,
  getSourceTranslation,
  isCheckInIntakeType,
  isIntakeWordingType,
  mapPersonProfileToIntakeCartPayload,
};
